<template>
  <SideMenu
    ref="sideMenu"
    :heading="activeTitle"
    :side="isMobile ? 'right' : 'left'"
    :active-secondary-button="childrenActive"
    @on-secondary-click="unsetChildren"
    @on-side-menu-close="$emit('onFlyoutMenuClose')"
  >
    <div class="p-24 relative overflow-hidden">
      <transition :name="transitionName">
        <div :key="history.length">
          <div
            v-for="(flyoutLink, idx) in activeLinks"
            :key="
              flyoutLink.title
                ? 'flyoutLink-' + flyoutLink.title
                : 'flyoutLink' + flyoutLink.type + idx
            "
            :class="{
              'h-32 flex w-full items-center mt-16 first:mt-0':
                flyoutLink.type === 'link',
              'mt-24':
                flyoutLink.type === 'divider' ||
                (activeLinks[idx - 1] && activeLinks[idx - 1].type === 'divider'),
            }"
          >
            <div
              v-if="flyoutLink.type === 'link'"
              class="w-full select-none cursor-pointer"
            >
              <div
                v-if="flyoutLink.children && flyoutLink.children.length > 0"
                @click="setChildren(flyoutLink.children, flyoutLink.title)"
              >
                <div class="flex flex-1 justify-between">
                  <div class="flex items-center text-grey">
                    <img
                      v-if="flyoutLink.icon"
                      :src="flyoutLink.icon"
                      class="rounded-full h-32 w-32 mr-12"
                    >
                    {{ flyoutLink.title }}
                  </div>
                  <img src="~/assets/icons/arrow-right.png" class="h-20 w-20">
                </div>
              </div>
              <a v-else-if="flyoutLink.external" :href="flyoutLink.url">
                <div class="flex flex-1 justify-between">
                  <div class="flex items-center text-grey">
                    <img
                      v-if="flyoutLink.icon"
                      :src="flyoutLink.icon"
                      class="rounded-full h-32 w-32 mr-12"
                    >
                    {{ flyoutLink.title }}
                  </div>
                </div>
              </a>
              <nuxt-link
                v-else
                :to="flyoutLink.url || '/'"
                @click.native="$emit('onFlyoutMenuClose')"
              >
                <div class="flex flex-1 justify-between">
                  <div class="flex items-center text-grey">
                    <img
                      v-if="flyoutLink.icon"
                      :src="flyoutLink.icon"
                      class="rounded-full h-32 w-32 mr-12"
                    >
                    {{ flyoutLink.title }}
                  </div>
                </div>
              </nuxt-link>
            </div>
            <div v-else-if="flyoutLink.type === 'divider'" class="border-b border-b-black-25" />
          </div>
        </div>
      </transition>
    </div>
  </SideMenu>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { type NavigationType } from '~/constants/types/navigation';
import SideMenu, { type SideMenuInterface } from '~/components/navigation/SideMenu.vue';

export default defineComponent({
  components: {
    SideMenu,
  },
  props: {
    links: {
      type: Array as PropType<NavigationType[]>,
      required: true,
    },
    isMobile: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['onFlyoutMenuClose'],
  data() {
    // History stack of visited levels, so the back button can step up one
    // level at a time (level 3 -> level 2 -> level 1) instead of always
    // jumping straight back to the root.
    return {
      history: [
        { links: this.links, title: this.$lang('header', 'menu') },
      ] as Array<{ links: NavigationType[]; title: string }>,
      // Tracks whether we're drilling in (push) or going back (pop), so the
      // transition can slide the new level in from the right or left.
      direction: 'push' as 'push' | 'pop',
    };
  },
  computed: {
    activeLinks(): NavigationType[] {
      return this.history[this.history.length - 1].links;
    },
    activeTitle(): string {
      // At root level just show the default heading (e.g. "Menu"). Once
      // drilled in, show the full path (e.g. "Dam > Underställ") so it's
      // clear which level/parent the current list belongs to.
      if (this.history.length === 1) {
        return this.history[0].title;
      }
      return this.history.slice(1).map((level) => level.title).join(' > ');
    },
    childrenActive(): boolean {
      return this.history.length > 1;
    },
    transitionName(): string {
      return this.direction === 'push' ? 'flyout-push' : 'flyout-pop';
    },
  },
  mounted() {
    this.toggleSideMenu();
  },
  methods: {
    toggleSideMenu() {
      const sideMenu = this.$refs.sideMenu as SideMenuInterface;
      sideMenu.openOverlay();
    },
    setChildren(links: NavigationType[], title: string) {
      this.direction = 'push';
      this.history.push({ links, title });
    },
    unsetChildren() {
      if (this.history.length > 1) {
        this.direction = 'pop';
        this.history.pop();
      }
    },
  },
});
</script>

<style scoped lang="postcss">
/*
 * Only the leaving (old) panel is taken out of flow (position: absolute),
 * so the entering (new) panel stays static and keeps driving the
 * container's real height/scroll - this avoids the container collapsing
 * to 0 height (which would clip everything via overflow: hidden) while
 * both panels briefly overlap during the slide.
 */
.flyout-push-leave-active,
.flyout-pop-leave-active {
  transition: transform 0.25s ease;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
.flyout-push-enter-active,
.flyout-pop-enter-active {
  transition: transform 0.25s ease;
}

.flyout-push-enter-from {
  transform: translateX(100%);
}
.flyout-push-leave-to {
  transform: translateX(-100%);
}

.flyout-pop-enter-from {
  transform: translateX(-100%);
}
.flyout-pop-leave-to {
  transform: translateX(100%);
}
</style>
